import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import "./inspection.css";
import { formElementTypes, orderStatus } from "../../../../utils/Constant";
import Loading from "../../../../components/loading/loading";
import {
  getOrderInspectionFormRequest,
  getOrderInspectionFormSuccess,
} from "../../../../redux/orders/slice";
import Element from "./element";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { uploadTempFileRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import TopLoader from "../../../../components/top-loader/top-loader";
import { RichTextReadOnly } from "mui-tiptap";
import useExtensions from "../../../../components/text-editor/use-extensions";

export default function Inspection({
  data,
  checkAnswers,
  showRequiredField,
}: any) {
  const dispatch = useDispatch();
  const extensions = useExtensions();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const childRef = useRef<{ onAddPhotoClick: () => void }>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [photoRequestOnly, setPhotoRequestOnly] = useState(false);
  const [photoRequestSections, setPhotoRequestSections] = useState<any[]>([]);
  const [addedImages, setAddedImages] = useState<any>();
  const [elementId, setElementId] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentImageCount, setCurrentImageCount] = useState(0);
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [canTakePhoto, setCanTakePhoto] = useState<any>([]);

  const { loading, orderInspectionForm } = useSelector(
    (state: any) => state.orders
  );

  useEffect(() => {
    dispatch(getOrderInspectionFormSuccess({ response: null }));
    if (data) {
      dispatch(
        getOrderInspectionFormRequest({
          orderId: data?.id,
        })
      );
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (orderInspectionForm) {
      setSections(orderInspectionForm);
      checkSectionAnswers(orderInspectionForm);
      filterPhotoElements(orderInspectionForm);
    } else {
      setSections([]);
    }
  }, [orderInspectionForm]); // eslint-disable-line

  useEffect(() => {
    if (sections) {
      checkSectionAnswers(sections);
    }
  }, [sections]); // eslint-disable-line

  const filterPhotoElements = (sections: any[]) => {
    const photoSection: any[] = [];
    sections.forEach((sec) => {
      const elements: any[] = [];
      sec?.elements?.forEach((ele: any) => {
        if (ele?.type === formElementTypes.photoRequest) {
          elements.push(ele);
        }
      });
      if (elements.length > 0) {
        const data = {
          ...sec,
          elements,
        };
        photoSection.push(data);
      }
    });
    setPhotoRequestSections(photoSection);
  };

  const onAddPhotoClick = (elementId: string) => {
    setElementId(elementId);
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event?.target?.files?.length === 0) {
      return;
    }

    const fileKeys: string[] = [];
    const files = event.target.files || [];
    setTotalImageCount(event?.target?.files?.length || 0);
    for (let i = 0; i < files.length; i++) {
      if (files[i].size <= 10000000) {
        setShowLoading(true);
        setCurrentImageCount(i + 1);
        try {
          const response = await uploadTempFileRequest(files[i]);
          if (response && response.data) {
            setShowLoading(false);
            fileKeys.push(response.data);
          }
        } catch (error) {
          setShowLoading(false);
          console.log("Upload Image error - ", error);
        }
      } else {
        setShowErrorNotification(true);
        setErrorMessage("File size exceeds 10MB limit.");
      }
    }
    setAddedImages({ elementId, fileKeys });
  };

  const checkSectionAnswers = (sections: any[]) => {
    const isRequiredAnswered = checkRequiredAnswers(sections);
    checkAnswers(isRequiredAnswered);
  };

  const hasAnswer = (element: any) => {
    if (element.answers && element.answers?.inspector?.answer) {
      return true;
    }
    return false;
  };

  const checkRequiredAnswers = (sections: any[]) => {
    let isAnswered = true;
    outerLoop: for (const section of sections) {
      for (const element of section.elements) {
        if (
          element.type === formElementTypes.openEnd ||
          element.type === formElementTypes.singleSelect ||
          element.type === formElementTypes.multiSelect ||
          element.type === formElementTypes.singleDate
        ) {
          if (element?.responseRequired) {
            if (!hasAnswer(element)) {
              isAnswered = false;
              break outerLoop;
            }
          }
        } else if (element.type === formElementTypes.multiInput) {
          for (const child of element.childElements) {
            if (child.responseRequired) {
              if (!hasAnswer(child)) {
                isAnswered = false;
                break outerLoop;
              }
            }
          }
        } else if (
          element.type === formElementTypes.table ||
          element.type === formElementTypes.matrix
        ) {
          if (element?.responseRequired) {
            const findAnswer = element.childElements.filter(
              (child: any) => child.answers && child.answers?.inspector?.answer
            );
            if (findAnswer.length <= 0) {
              isAnswered = false;
              break outerLoop;
            }
          }
        } else if (element.type === formElementTypes.photoRequest) {
          if (element?.responseRequired) {
            if (
              !(
                element?.answers &&
                (element?.answers?.inspector?.answer ||
                  (element?.answers?.inspector?.photoAnswers &&
                    element?.answers?.inspector?.photoAnswers?.length > 0))
              )
            ) {
              isAnswered = false;
              break outerLoop;
            }
          }
        }
      }
    }
    return isAnswered;
  };

  const checkElementAnswerStatus = (element: any) => {
    if (element.type === formElementTypes.multiInput) {
      if (element.childElements && element.childElements.length > 0) {
        const allChildrenAnswered = element.childElements.every(
          (child: any) => {
            if (child.responseRequired) {
              return hasAnswer(child);
            }
            return true;
          }
        );
        return allChildrenAnswered;
      }
    } else if (element.type === formElementTypes.table) {
      let answered = false;
      if (element.childElements && element.childElements.length > 0) {
        element.childElements.forEach((child: any) => {
          if (hasAnswer(child)) {
            answered = true;
          }
        });
      }
      return answered;
    } else if (element.type === formElementTypes.photoRequest) {
      return (
        (element.answers && element.answers?.inspector?.answer) ||
        (element.answers &&
          element.answers?.inspector?.photoAnswers &&
          element.answers?.inspector?.photoAnswers.length > 0)
      );
    } else {
      return element.answers && element.answers?.inspector?.answer;
    }
  };

  const updateData = (
    sectionId: string,
    selectedElementId: string,
    elementType: string,
    payload: any,
    photoKey?: string
  ) => {
    setAddedImages([]);
    if (
      elementType === formElementTypes.openEnd ||
      elementType === formElementTypes.singleSelect ||
      elementType === formElementTypes.multiSelect ||
      elementType === formElementTypes.singleDate
    ) {
      setSections((prev) =>
        prev.map((section) =>
          section.id !== sectionId
            ? section
            : {
                ...section,
                elements: section.elements.map((element: any) =>
                  element.id !== selectedElementId
                    ? element
                    : {
                        ...element,
                        answers: {
                          ...element.answers,
                          inspector: {
                            ...element.answers?.inspector,
                            answer: payload?.answer,
                          },
                        },
                      }
                ),
              }
        )
      );
    } else if (
      elementType === formElementTypes.table ||
      elementType === formElementTypes.matrix ||
      elementType === formElementTypes.multiInput
    ) {
      const updatesMap = new Map(
        payload?.childElements.map((u: any) => [u.id, u.answer])
      );
      setSections((prev) =>
        prev.map((section) =>
          section.id !== sectionId
            ? section
            : {
                ...section,
                elements: section.elements.map((element: any) =>
                  element.id !== selectedElementId
                    ? element
                    : {
                        ...element,
                        childElements: element.childElements.map((child: any) =>
                          updatesMap.has(child.id)
                            ? {
                                ...child,
                                answers: {
                                  ...child.answers,
                                  inspector: {
                                    ...child.answers?.inspector,
                                    answer: updatesMap.get(child.id),
                                  },
                                },
                              }
                            : child
                        ),
                      }
                ),
              }
        )
      );
    } else if (elementType === formElementTypes.photoRequest) {
      if (photoKey) {
        const updatesMap = new Map(
          payload?.photoAnswer?.existingItems.map((u: any) => [
            u.key,
            u.description,
          ])
        );
        setSections((prev) =>
          prev.map((section) =>
            section.id !== sectionId
              ? section
              : {
                  ...section,
                  elements: section.elements.map((element: any) =>
                    element.id !== selectedElementId
                      ? element
                      : {
                          ...element,
                          answers: {
                            ...element.answers,
                            inspector: {
                              ...element.answers?.inspector,
                              photoAnswers:
                                element.answers?.inspector?.photoAnswers.map(
                                  (child: any) =>
                                    updatesMap.has(child.key)
                                      ? {
                                          ...child,
                                          description: updatesMap.get(
                                            child.key
                                          ),
                                        }
                                      : child
                                ),
                            },
                          },
                        }
                  ),
                }
          )
        );
      } else {
        dispatch(
          getOrderInspectionFormRequest({
            orderId: data?.id,
          })
        );
      }
    }
  };

  const checkPhotoSelection = (elementId: string) => {
    const filterPhotoElement = canTakePhoto.find(
      (photo: any) => photo?.elementId === elementId
    );
    if (filterPhotoElement) {
      return filterPhotoElement?.canTakePhoto;
    }
    return false;
  };

  return (
    <Box>
      <Box sx={{ mx: 3, mt: 2 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={photoRequestOnly}
                onChange={(e) => {
                  if (e.target.checked) {
                    filterPhotoElements(sections);
                  }
                  setPhotoRequestOnly(e.target.checked);
                }}
              />
            }
            label="Show Only Photo Requests"
          />
        </FormGroup>
      </Box>
      {photoRequestOnly ? (
        <Box sx={{ mx: 3, my: 2 }}>
          {photoRequestSections.map((section) => (
            <Accordion
              sx={{ backgroundColor: "#FAFAFA", mt: 2 }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={"510"}>
                    {section.title}
                  </Typography>
                  {section?.elements && section?.elements?.length > 0 && (
                    <Typography>
                      {section?.elements?.length} Questions
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mt: 0.5 }}>
                  {section?.introductionText}
                </Typography>
                {section?.elements.map((element: any) => (
                  <Box
                    key={element?.id}
                    className={`question-margin ${
                      element.type !== formElementTypes.textField
                        ? "question-layout"
                        : ""
                    }`}
                  >
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" fontWeight={"510"}>
                          {element?.settings?.title}
                        </Typography>
                        {element?.definedName && (
                          <Typography
                            ml={2}
                            sx={{ color: "#2E7D32", fontSize: 14 }}
                          >
                            #{element?.definedName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {element?.type === formElementTypes.photoRequest && (
                          <>
                            <Button
                              sx={{ mr: 2 }}
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                onAddPhotoClick(element?.id);
                              }}
                              disabled={
                                orderStatus[
                                  data?.orderStatus as keyof typeof orderStatus
                                ] < 41 || checkPhotoSelection(element?.id)
                              }
                            >
                              Add Photo
                            </Button>
                            <input
                              hidden
                              multiple
                              accept="image/*"
                              type="file"
                              ref={fileInputRef}
                              onChange={handleImageUpload}
                              onClick={(event) => {
                                // same file selection issue
                                (event.target as HTMLInputElement).value = "";
                              }}
                            />
                          </>
                        )}
                        {checkElementAnswerStatus(element) && (
                          <CheckCircleOutlinedIcon color="success" />
                        )}
                      </Box>
                    </Box>
                    {element?.type === formElementTypes.textField ? (
                      <RichTextReadOnly
                        content={element?.settings?.text}
                        extensions={extensions}
                      />
                    ) : (
                      <Typography sx={{ mt: 0.5 }}>
                        {element?.settings?.text || ""}
                      </Typography>
                    )}
                    <Element
                      ref={childRef}
                      key={element?.id}
                      sectionId={section?.id}
                      element={element}
                      orderId={data?.id}
                      addedImages={addedImages}
                      updateData={updateData}
                      updateCanTakePhoto={(
                        elementId: string,
                        takePhoto: boolean
                      ) => {
                        if (canTakePhoto.length > 0) {
                          const findPhoto = canTakePhoto.find(
                            (photo: any) => photo.elementId === elementId
                          );
                          if (findPhoto) {
                            setCanTakePhoto((prev: any) =>
                              prev.map((photo: any) =>
                                photo?.elementId === elementId
                                  ? {
                                      ...photo,
                                      canTakePhoto: takePhoto,
                                    }
                                  : { ...photo }
                              )
                            );
                          } else {
                            setCanTakePhoto((prev: any) => [
                              ...prev,
                              { elementId, canTakePhoto: takePhoto },
                            ]);
                          }
                        } else {
                          setCanTakePhoto([
                            { elementId, canTakePhoto: takePhoto },
                          ]);
                        }
                      }}
                      disabled={
                        orderStatus[
                          data?.orderStatus as keyof typeof orderStatus
                        ] < 41 ||
                        orderStatus[
                          data?.orderStatus as keyof typeof orderStatus
                        ] > 44
                      }
                      showRequiredField={showRequiredField}
                    />
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Box sx={{ mx: 3, my: 2 }}>
          {sections.map((section) => (
            <Accordion
              sx={{ backgroundColor: "#FAFAFA", mt: 2 }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Box className="section-header">
                  <Typography variant="h6" fontWeight={"510"}>
                    {section.title}
                  </Typography>
                  {section?.elements && section?.elements?.length > 0 && (
                    <Typography>
                      {section?.elements?.length} Questions
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mt: 0.5 }}>
                  {section?.introductionText}
                </Typography>
                {section?.elements.map((element: any) => (
                  <Box
                    key={element?.id}
                    className={`question-margin ${
                      element.type !== formElementTypes.textField
                        ? "question-layout"
                        : ""
                    }`}
                  >
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="h6" fontWeight={"510"}>
                          {element?.settings?.title}
                        </Typography>
                        {element?.definedName && (
                          <Typography
                            ml={2}
                            sx={{ color: "#2E7D32", fontSize: 14 }}
                          >
                            #{element?.definedName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {element?.type === formElementTypes.photoRequest && (
                          <>
                            <Button
                              sx={{ mr: 2 }}
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                onAddPhotoClick(element?.id);
                              }}
                              disabled={
                                orderStatus[
                                  data?.orderStatus as keyof typeof orderStatus
                                ] < 41 || checkPhotoSelection(element?.id)
                              }
                            >
                              Add Photo
                            </Button>
                            <input
                              hidden
                              multiple
                              accept="image/*"
                              type="file"
                              ref={fileInputRef}
                              onChange={handleImageUpload}
                              onClick={(event) => {
                                // same file selection issue
                                (event.target as HTMLInputElement).value = "";
                              }}
                            />
                          </>
                        )}
                        {checkElementAnswerStatus(element) && (
                          <CheckCircleOutlinedIcon color="success" />
                        )}
                      </Box>
                    </Box>
                    {element?.type === formElementTypes.textField ? (
                      <RichTextReadOnly
                        content={element?.settings?.text}
                        extensions={extensions}
                      />
                    ) : (
                      <Typography sx={{ mt: 0.5 }}>
                        {element?.settings?.text || ""}
                      </Typography>
                    )}
                    <Element
                      ref={childRef}
                      key={element?.id}
                      sectionId={section?.id}
                      element={element}
                      orderId={data?.id}
                      addedImages={addedImages}
                      updateData={updateData}
                      updateCanTakePhoto={(
                        elementId: string,
                        takePhoto: boolean
                      ) => {
                        if (canTakePhoto.length > 0) {
                          const findPhoto = canTakePhoto.find(
                            (photo: any) => photo.elementId === elementId
                          );
                          if (findPhoto) {
                            setCanTakePhoto((prev: any) =>
                              prev.map((photo: any) =>
                                photo?.elementId === elementId
                                  ? {
                                      ...photo,
                                      canTakePhoto: takePhoto,
                                    }
                                  : { ...photo }
                              )
                            );
                          } else {
                            setCanTakePhoto((prev: any) => [
                              ...prev,
                              { elementId, canTakePhoto: takePhoto },
                            ]);
                          }
                        } else {
                          setCanTakePhoto([
                            { elementId, canTakePhoto: takePhoto },
                          ]);
                        }
                      }}
                      disabled={
                        orderStatus[
                          data?.orderStatus as keyof typeof orderStatus
                        ] < 41 ||
                        orderStatus[
                          data?.orderStatus as keyof typeof orderStatus
                        ] > 44
                      }
                      showRequiredField={showRequiredField}
                    />
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {loading && <TopLoader />}
      {showLoading && (
        <Loading
          title={`Uploading ${currentImageCount} of ${totalImageCount}`}
        />
      )}
    </Box>
  );
}
